'use client';

import type { GetMarketsRow } from '@morfi/backend-sdk-v2/client';

/**
 * The featured-market-hero subtree consumes a whole market row at once — it is
 * a single-slide presentation surface, not a list. The carousel parent owns a
 * fixed-DTO `useGetMarkets` query and hands each hero
 * slide one `GetMarketsRow`, re-keyed by pubkey.
 *
 * `HeroMarket` is the v2 row shape (`GetMarketsRow` with `outcomes` guaranteed
 * present). The fixed MarketCardDto always includes `outcomes`, so the optional
 * `?` on the transitional generated row type is narrowed away here.
 */
export type HeroMarketOutcome = NonNullable<GetMarketsRow['outcomes']>[number];

export type HeroMarket = Omit<GetMarketsRow, 'outcomes'> & {
  readonly outcomes: readonly HeroMarketOutcome[];
};

/** Local declaration of fields consumed by the hero carousel. */
export const HERO_MARKET_FIELDS = [
  'pubkey',
  'title',
  'status',
  'volumeTotal',
  'prizePool',
  'createdAt',
  'resolvedAt',
  'marketConfig',
  'uniqueTradersTotal',
  'convictionUpdatedAt',
  'outcomeCount',
  'outcomes',
] as const;

/**
 * Local declaration of fields consumed from the hero's NATS-live
 * `useGetMarket` detail row. The fixed detail DTO needs no wire projection.
 */
export const HERO_LIVE_FIELDS = [
  'outcomes',
  'volumeTotal',
  'prizePool',
  'uniqueTradersTotal',
  'convictionUpdatedAt',
  'outcomeCount',
  'tradingStartedAt',
  'creatorProfile',
] as const;
